/**
 * Keyed repeat reconciliation.
 *
 * `repeat()` items commit through this module: a keyed entry map reuses
 * compatible template instances, removals run before placement under an
 * ancestor-detach window (#1311), and minimal-move placement (LIS over
 * retained old positions) moves only rows that are out of place — preserving
 * focus, selection, and scroll state. Initial population takes a dedicated
 * fast path: every key is new and placement is a sequential append, so the
 * per-key diff bookkeeping and the LIS buy nothing there (#1443).
 */
import { destroyMaterialized, destroyRepeatEntry, emptyRangeAtOnce, rangeParentOf, releasePlacedFragment, withSubtreeDetach, } from './render-dom-range.js';
import { materializeValue } from './render-dom-nodes.js';
import { createOwner, resetOwner, runWithOwner } from './owner.js';
import { isTemplateInstanceValue, renderRow, resolveValue, rowReadsIndex, runtimeError, } from './render-values.js';
/**
 * Reorder fast-path scratch (#1451 L3), shared across every repeat part and
 * grown to the largest row count seen — one buffer set for the process, never
 * per row and never per pass, so the #1323 keyed-row ceilings are untouched.
 * Owned under `scratchDepth`: a nested repeat reconciling while an outer scan
 * holds the buffers sees the nonzero depth and takes the general diff, which
 * allocates its own.
 */
let scratchDepth = 0;
let scratchCapacity = 0;
let scanGeneration = 0;
/** Displaced entry per position this scan; read only where the mark says so. */
let scratchVals = [];
/** Old 1-based position per new position; negative marks a displaced entry. */
let scratchSeq = new Int32Array();
let scratchPred = new Int32Array();
let scratchTails = new Int32Array();
function ensureScratch(length) {
    if (length <= scratchCapacity)
        return;
    const capacity = Math.max(length, scratchCapacity * 2, 64);
    scratchVals = Array.from({ length: capacity }, () => undefined);
    scratchSeq = new Int32Array(capacity);
    scratchPred = new Int32Array(capacity);
    scratchTails = new Int32Array(capacity + 1);
    scratchCapacity = capacity;
}
export function updateRepeatPart(part, repeatValue) {
    destroyMaterialized(part.materialized);
    part.materialized = null;
    part.textNode = null;
    if (!part.documentObj || !(part.rangeParent || (part.anchor && part.end)))
        return;
    const documentObj = part.documentObj;
    // A node part carries no keyed-entry map until a repeat descriptor reaches
    // it: the two text holes in every row of a 1,000-row list would otherwise
    // each allocate one they never read. This commit is that part's first pass,
    // so every key reads as new — which is what an empty map says too — and the
    // pass assigns `part.entries` wholesale before it returns.
    const priorEntries = part.entries;
    const domAdapter = part.domAdapter;
    if (!domAdapter)
        return;
    const items = Array.from(resolveValue(repeatValue.items) || []);
    // Read once per reconciliation, not per row: whether this list's rows can
    // observe their own position at all (#1330).
    const readsIndex = rowReadsIndex(repeatValue.templateFn);
    // Initial population (first pass, or re-populating after a clear): every
    // key is new and placement is a sequential append, so the diff bookkeeping —
    // per-key Map.get misses, the duplicate-key Set, and a LIS over all-zero
    // positions — buys nothing (#1443 board — create1k is the largest behind
    // claim). The duplicate-key check rides the output map instead of a second
    // Set.
    if (!priorEntries || priorEntries.size === 0) {
        const nextEntries = new Map();
        const parent = rangeParentOf(part);
        // A detached part has nowhere to place rows: record nothing, create
        // nothing (the general path materialized rows first and orphaned them).
        if (!parent)
            return;
        const end = part.end ?? null;
        const order = [];
        for (let index = 0; index < items.length; index += 1) {
            const item = items[index];
            const key = repeatValue.keyFn(item, index);
            if (nextEntries.has(key)) {
                throw runtimeError('RT410', `Duplicate keyed-sequence key "${String(key)}"`);
            }
            const rowScope = createOwner();
            const rendered = runWithOwner(rowScope, () => renderRow(repeatValue.templateFn, item, index));
            const materialized = materializeValue(rendered, documentObj, domAdapter, false, true);
            materialized.mount?.();
            // Every field in the literal: an entry keeps ONE hidden class for its
            // whole life, so neither the create path nor the general diff below
            // pays per-row map transitions for the fields they used to bolt on
            // afterwards (#1598's PropertyArray rule, applied to the row entry).
            const entry = {
                materialized,
                nodes: materialized.nodes,
                item,
                index,
                prev: 0,
                scan: undefined,
                scope: rowScope,
            };
            nextEntries.set(key, entry);
            order.push(entry);
            insertEntryNodes(parent, entry, end);
        }
        part.entries = nextEntries;
        part.entryOrder = order;
        const nodes = [];
        for (const entry of nextEntries.values()) {
            for (const node of entry.nodes)
                nodes.push(node);
        }
        part.nodes = nodes;
        return;
    }
    // Reorder fast path (#1451 L3): the list may be an identity — same items in
    // the same order — or a permutation of the retained entries, and both are
    // provable with array reads and zero Map traffic. `entryOrder` mirrors the
    // entry map in item order (every writer below maintains the pair), so
    // position i pairs `items[i]` with the entry that held position i last pass.
    // The scan allocates nothing: matched positions stamp their entry and record
    // their old slot; displaced positions look the moved entry up by key. A new
    // key, a changed item, an index-reading row among the displaced, any length
    // mismatch, or a busy scratch falls through to the general diff, which
    // re-derives everything it needs from the untouched map.
    const entryOrder = part.entryOrder;
    if (scratchDepth === 0 &&
        entryOrder !== undefined &&
        entryOrder.length === items.length &&
        items.length > 0 &&
        // Arity-1 keyFn: the key follows item identity — the premise of keying —
        // so a permutation of the items is a permutation of the same keys and the
        // map below survives untouched. The same arity contract #1330 relies on
        // for row functions. Placement needs a live parent; without one the
        // general diff's detached handling applies.
        repeatValue.keyFn.length <= 1 &&
        rangeParentOf(part) !== null) {
        scratchDepth += 1;
        let identity = false;
        let displaced = 0;
        try {
            ensureScratch(items.length);
            scanGeneration += 1;
            const generation = scanGeneration;
            identity = true;
            for (let index = 0; index < items.length; index += 1) {
                const item = items[index];
                const held = entryOrder[index];
                if (held !== undefined && held.item === item) {
                    // Same item at its old slot. A second claim on the same entry means
                    // the items array holds a duplicate — the same RT410 the general
                    // diff raises when a key lands twice.
                    if (held.scan === generation) {
                        throw runtimeError('RT410', `Duplicate keyed-sequence key "${String(repeatValue.keyFn(item, index))}"`);
                    }
                    held.scan = generation;
                    scratchSeq[index] = index + 1;
                    continue;
                }
                // A different item at this slot: either a retained entry moved here,
                // or the list is not a permutation and the general diff must run.
                // Index-reading rows re-render when they move (#1330), so a move with
                // `readsIndex` is out of the fast path's reach by construction.
                if (readsIndex) {
                    identity = false;
                    break;
                }
                const key = repeatValue.keyFn(item, index);
                const moved = priorEntries.get(key);
                if (moved === undefined || moved.item !== item || moved.scan === generation) {
                    if (moved !== undefined && moved.item === item && moved.scan === generation) {
                        throw runtimeError('RT410', `Duplicate keyed-sequence key "${String(key)}"`);
                    }
                    identity = false;
                    break;
                }
                moved.scan = generation;
                scratchSeq[index] = -(moved.index ?? 0) - 1;
                scratchVals[index] = moved;
                displaced += 1;
            }
        }
        finally {
            scratchDepth -= 1;
        }
        if (identity) {
            // Identity pass: every item is the entry it already sits under, so the
            // DOM, the map, the order and the indexes are all already correct — the
            // general diff would rebuild all of them to change none.
            if (displaced === 0)
                return;
            // Permutation pass: commit the new order into the mirror array, restamp
            // the moved entries' indexes, and place only what the subsequence
            // declines to keep. The map is untouched — same keys, same entries.
            for (let index = 0; index < items.length; index += 1) {
                const mark = scratchSeq[index] ?? 0;
                if (mark >= 0)
                    continue;
                const moved = scratchVals[index];
                if (moved === undefined)
                    continue;
                entryOrder[index] = moved;
                moved.index = index;
                scratchSeq[index] = -mark;
            }
            const parent = rangeParentOf(part);
            if (parent) {
                const lisLength = longestIncreasingSubsequence(scratchSeq, items.length, scratchPred, scratchTails);
                let keep = scratchTails[lisLength] ?? 0;
                let chain = lisLength;
                let cursor = part.end ?? null;
                for (let index = items.length - 1; index >= 0; index -= 1) {
                    const entry = entryOrder[index];
                    if (entry === undefined)
                        continue;
                    if (chain > 0 && index === keep) {
                        chain -= 1;
                        keep = scratchPred[keep] ?? 0;
                    }
                    else {
                        insertEntryNodes(parent, entry, cursor);
                    }
                    cursor = entry.nodes[0] ?? cursor;
                }
                const nodes = [];
                for (const entry of entryOrder) {
                    for (const node of entry.nodes)
                        nodes.push(node);
                }
                part.nodes = nodes;
            }
            return;
        }
    }
    const entries = priorEntries;
    const nextEntries = new Map();
    items.forEach((item, index) => {
        const key = repeatValue.keyFn(item, index);
        if (nextEntries.has(key)) {
            throw runtimeError('RT410', `Duplicate keyed-sequence key "${String(key)}"`);
        }
        let entry = entries.get(key);
        // An unchanged item cannot render differently, so skip templateFn
        // entirely: it is user code that allocates a fresh instance (and often
        // per-row computeds) which updateEntry would then discard after copying
        // its bindings. Changing one row in a 1,000-row list used to run all 1,000
        // row functions (#677). Identity is the contract — mutating an item in
        // place without a new reference is outside it, which is what keying a list
        // by identity already means.
        //
        // The index only joins that test for a row that declared it (#1330): a row
        // function of arity 1 has no way to observe its position, so a shifted
        // survivor is skipped as well. Removing every tenth of 1,000 rows moves 891
        // survivors, and every one of them used to re-render to produce identical
        // output. `entry.index` is restamped even on the skip so it never lies —
        // the same list may be reconciled against a row function of either shape.
        if (entry && entry.item === item && (!readsIndex || entry.index === index)) {
            entry.index = index;
            nextEntries.set(key, entry);
            return;
        }
        // The row renders inside its own owner scope (#1310), so the computeds and
        // cleanups it creates belong to the row rather than to this effect's
        // per-run owner (#1203) — which is torn down on the next reconciliation
        // while the skip above declines to rebuild what that teardown destroyed.
        // A re-render RESETS the scope instead of replacing it: the previous
        // generation's computations go, but `updateEntry` may keep the instance
        // built under it alive, and that instance must not lose its context
        // ancestor.
        const scope = entry?.scope;
        if (scope)
            resetOwner(scope);
        const rowScope = scope ?? createOwner();
        const rendered = runWithOwner(rowScope, () => renderRow(repeatValue.templateFn, item, index));
        if (!entry || !updateEntry(entry, rendered)) {
            if (entry)
                destroyMaterialized(entry.materialized);
            // A keyed repeat's row: more instantiations of this template are already
            // known to be coming, so its parse is worth caching on the first (#1375).
            const materialized = materializeValue(rendered, documentObj, domAdapter, false, true);
            // Same one-hidden-class literal as the create path above: scope, item,
            // index, prev and scan are declared here, not bolted on below.
            entry = {
                materialized,
                nodes: materialized.nodes,
                item,
                index,
                prev: 0,
                scan: undefined,
                scope: rowScope,
            };
            materialized.mount?.();
        }
        entry.scope = rowScope;
        entry.item = item;
        entry.index = index;
        nextEntries.set(key, entry);
    });
    const parent = rangeParentOf(part);
    if (!parent) {
        return;
    }
    // Removals run before placement so a deleted entry's stale nodes can never
    // sit at the placement cursor and force re-inserting every later block. The
    // same walk stamps each retained entry's old position for the subsequence
    // pass below.
    //
    // Teardown runs under an ancestor-detach window (#1311): an entry's own node
    // set is removed here, so nothing nested inside it — a row's text parts, a
    // nested repeat, a nested instance — issues a removal of its own. Dropping
    // every entry empties the part's whole range, which is one bulk call rather
    // than one call per node.
    const clearingAll = nextEntries.size === 0 && entries.size > 0;
    withSubtreeDetach(() => {
        if (!clearingAll) {
            // Old positions come from the item-order mirror, not map iteration: the
            // reorder fast path leaves the map untouched, so its insertion order is
            // no longer guaranteed to be the rows' DOM order (#1451 L3). The mirror
            // is what every writer keeps aligned with the live row order. A pass
            // that clears everything has no reader — nextEntries is empty, so the
            // subsequence never forms and the next pass re-stamps.
            let oldIndex = 0;
            for (const entry of part.entryOrder ?? []) {
                oldIndex += 1;
                entry.prev = oldIndex;
            }
        }
        for (const [key, entry] of entries) {
            if (nextEntries.has(key))
                continue;
            // Removed keys own both lifecycle cleanup and DOM removal.
            destroyRepeatEntry(entry);
            if (clearingAll)
                continue;
            for (const node of entry.nodes) {
                if (node.parentNode)
                    node.parentNode.removeChild(node);
            }
        }
    });
    if (clearingAll)
        emptyRangeAtOnce(part);
    // Minimal-move placement: entries on the longest increasing subsequence of
    // retained old positions keep their DOM nodes; only off-subsequence and new
    // entries are inserted, before a cursor walked backward from the end
    // boundary. prev is 1-based so 0 marks entries that are new (or freshly
    // re-materialized) this pass — never on the subsequence, always inserted.
    // The old forward cursor re-inserted every node after the first
    // displacement, which is what the remove/swap benchmarks measured.
    const nextVals = [];
    const seq = new Int32Array(nextEntries.size);
    let i = 0;
    for (const entry of nextEntries.values()) {
        nextVals.push(entry);
        seq[i] = entry.prev ?? 0;
        entry.prev = 0;
        i += 1;
    }
    const predecessor = new Int32Array(seq.length);
    const tails = new Int32Array(seq.length + 1);
    const lisLength = longestIncreasingSubsequence(seq, seq.length, predecessor, tails);
    // Backtracking from the longest tail visits kept indices in strictly
    // decreasing order — exactly this reverse walk's order — so the chain is
    // consumed during placement instead of materializing a membership set.
    let keep = tails[lisLength] ?? 0;
    let chain = lisLength;
    // The end marker is where the reverse walk starts inserting; a range-parented
    // part (#1354) has none, and `null` is the same instruction — insert at the
    // parent's end. A sibling-bounded part (#1390) ends at a static element or
    // whitespace text node, so the cursor is a plain Node.
    let cursor = part.end ?? null;
    for (i = nextVals.length - 1; i >= 0; i -= 1) {
        const entry = nextVals[i];
        if (!entry)
            continue;
        if (chain > 0 && i === keep) {
            chain -= 1;
            keep = predecessor[keep] ?? 0;
        }
        else {
            insertEntryNodes(parent, entry, cursor);
        }
        // One expression rather than a checked-then-used local: an entry with no
        // nodes leaves the cursor where it was, which is what the guard said. A DOM
        // node is never falsy, so `??` and the truthiness test agree, and UBS reads
        // the guarded shape as an unsafe narrowing.
        cursor = entry.nodes[0] ?? cursor;
    }
    part.entries = nextEntries;
    // nextVals is the new item order — exactly what the mirror array holds.
    part.entryOrder = nextVals;
    // Single pass: the spread built a full array of entries only to flatMap it
    // into a second one, so a 1,000-row list allocated both on every update.
    const nodes = [];
    for (const entry of nextVals) {
        for (const node of entry.nodes)
            nodes.push(node);
    }
    part.nodes = nodes;
}
/**
 * Patience longest-increasing-subsequence over retained old positions.
 *
 * Shared by both placement passes: the general diff (which allocates its
 * scratch per call, exactly `length` wide) and the reorder fast path below
 * (which reuses the module scratch, whose capacity outlives any one list).
 * `length` bounds the walk, because a reusable `seq` carries stale slots past
 * `length` that would otherwise join the subsequence. `seq` holds 1-based old
 * positions, 0 for entries new this pass; zeros never join. `predecessor[i]`
 * receives the previous index on the subsequence through `i`, `tails` the
 * per-length best index — both caller-owned so neither pass allocates under
 * the other's rules.
 */
function longestIncreasingSubsequence(seq, length, predecessor, tails) {
    let lisLength = 0;
    for (let i = 0; i < length; i += 1) {
        const value = seq[i] ?? 0;
        if (value === 0)
            continue;
        let low = 1;
        let high = lisLength;
        while (low <= high) {
            const mid = (low + high) >>> 1;
            if ((seq[tails[mid] ?? 0] ?? 0) < value)
                low = mid + 1;
            else
                high = mid - 1;
        }
        predecessor[i] = tails[low - 1] ?? 0;
        tails[low] = i;
        if (low > lisLength)
            lisLength = low;
    }
    return lisLength;
}
/**
 * Places one entry's nodes before `cursor`.
 *
 * A row materialized this pass still has every node parented to the private
 * fragment it was mounted into, and inserting that fragment moves all of them
 * in one tree mutation instead of one per node — the difference between
 * 1 and N `insertBefore` calls per row, and between 1 and N mutation records.
 * The fragment is only usable while it still holds exactly this entry's nodes:
 * placement empties it, so a later pass that moves the same entry (a swap, or
 * a removal shifting it) proves the claim false and moves node by node.
 */
function insertEntryNodes(parent, entry, cursor) {
    const pending = entry.materialized.fragment;
    if (pending !== undefined &&
        pending.childNodes.length === entry.nodes.length &&
        pending.firstChild === (entry.nodes[0] ?? null)) {
        parent.insertBefore(pending, cursor);
        // The claim is spent the instant it is used, and a 1,000-row list that
        // keeps them holds 1,000 drained fragments (#1335).
        releasePlacedFragment(entry.materialized, parent);
        return;
    }
    for (const node of entry.nodes) {
        parent.insertBefore(node, cursor);
    }
    releasePlacedFragment(entry.materialized, parent);
}
function updateEntry(entry, rendered) {
    const current = entry.materialized.instance;
    if (current && isTemplateInstanceValue(rendered) && current.template === rendered.template) {
        current.update(rendered.bindings);
        return true;
    }
    return false;
}
//# sourceMappingURL=render-dom-repeat.js.map